import dayjs from "dayjs";
import ReactDOMServer from "react-dom/server";

import {
    shortMonthAndDay,
    shortMonthAndShortYear,
    combineConfigs,
    changeBellWithoutTooltip,
    getIntervalConfig,
} from "@similarweb/pro-logic-components";
import { rgba, colorsPalettes } from "@similarweb/styles";

import { swSettings } from "app/common/services/swSettings";
import { translate } from "app/filters/i18nFilter";
import { abbrNumberFilter } from "app/filters/ngFilters";
import type { IPoint } from "app/pages/keyword-analysis/utils/AnnotationBanner";
import { displayAnnotation, getAnnotationDateForGranularity } from "app/pages/keyword-analysis/utils/AnnotationBanner";
import type { useGraphTooltip } from "app/pages/keyword-research/keyword-analysis/common-components/clicks-over-time-graph/useGraphTooltip";
import { BasicDurations } from "app/services/DurationService";
import { chartTypes } from "app/UtilitiesAndConstants/Constants/ChartTypes";
import { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";
import type { ValueOf } from "pro-features/types";

const dateToUTC = (dateString: string) => {
    if (dateString) {
        const date = dateString.split("-");
        return Date.UTC(parseInt(date[0], 10), parseInt(date[1], 10) - 1, parseInt(date[2], 10));
    }
};

export const getChartConfig = (
    timeGranularity: GranularityNames,
    is28Days: boolean,
    graphDuration: ValueOf<typeof BasicDurations>,
    tooltip: ReturnType<typeof useGraphTooltip>,
) => {
    const dateForAnnotationDay = swSettings.current?.resources?.VersionRerurnDate__1d6d228 as string;

    const dateForAnnotation = getAnnotationDateForGranularity(dateForAnnotationDay, timeGranularity);

    const format = timeGranularity === GranularityNames.MONTHLY ? shortMonthAndShortYear : shortMonthAndDay;
    // @ts-expect-error FIXME: Argument of type 'number' is not assignable to parameter of type 'boolean'
    const yAxisFormatter = ({ value }) => abbrNumberFilter()(value, 0);
    const xAxisFormatter = ({ value }) => dayjs.utc(value).format(format);
    const KeywordAnalysisAvailableFromDate = dateToUTC(
        swSettings.components.KeywordAnalysisV2Organic.resources.KeywordAnalysisAvailableFromDate,
    );
    const alertMessageForKeywordAnalysisAvailableFromDate = "keyword.analysis.graph.notification.alert.start.date";

    return combineConfigs({ type: chartTypes.LINE }, [
        getIntervalConfig(timeGranularity),
        changeBellWithoutTooltip({
            changeStartDate: dayjs.utc(KeywordAnalysisAvailableFromDate).toString(),
            removeLine: true,
        }),
        changeBellWithoutTooltip({ changeStartDate: dayjs(dateForAnnotation).toString(), removeLine: true }),
        {
            tooltip: {
                shared: true,
                outside: true,
                useHTML: true,
                formatter: function () {
                    return ReactDOMServer.renderToString(
                        tooltip(
                            this.index,
                            this.x,
                            KeywordAnalysisAvailableFromDate === this.points[0].x &&
                                alertMessageForKeywordAnalysisAvailableFromDate,
                            displayAnnotation(
                                this.points as unknown as IPoint[],
                                translate("keyword.research.improved.algorithms.annotation.text"),
                                getAnnotationDateForGranularity(dateForAnnotationDay, timeGranularity),
                            ),
                        ),
                    );
                },
            },
        },
        {
            chart: {
                height: 200,
                marginTop: 27,
                marginBottom: 30,
            },
            plotOptions: {
                area: {
                    marker: {
                        enabled: false,
                    },
                    stacking: "normal",
                    connectNulls: true,
                    fillOpacity: 1,
                    lineWidth: 0.5,
                },
            },
            yAxis: [
                {
                    labels: {
                        formatter: yAxisFormatter,
                    },
                    title: {
                        text: null,
                    },
                    gridLineColor: rgba(colorsPalettes.carbon[100], 0.4),
                    gridLineWidth: 1,
                    gridLineDashStyle: "Solid",
                    lineWidth: 0,
                    reversed: false,
                    allowDecimals: true,
                    startOnTick: false,
                    endOnTick: true,
                    tickPositioner: null,
                    gridZIndex: 4,
                    showFirstLabel: true,
                    showLastLabel: true,
                    min: 0,
                    max: undefined,
                    tickAmount: undefined,
                },
                {
                    labels: {
                        enabled: false,
                    },
                    title: {
                        text: null,
                    },
                    gridLineColor: rgba(colorsPalettes.carbon[100], 0.4),
                    gridLineWidth: 1,
                    gridLineDashStyle: "Solid",
                    lineWidth: 0,
                    reversed: false,
                    allowDecimals: true,
                    startOnTick: false,
                    endOnTick: true,
                    tickPositioner: null,
                    gridZIndex: 4,
                    showFirstLabel: false,
                    showLastLabel: true,
                },
            ],
            xAxis: {
                gridLineWidth: 0,
                labels: {
                    formatter: xAxisFormatter,
                },
                plotLines: [
                    is28Days && graphDuration !== BasicDurations.LAST_TWENTY_EIGHT_DAYS
                        ? {
                              color: colorsPalettes.carbon[200],
                              width: 1,
                              dashStyle: "ShortDash",
                              value: dayjs.utc().subtract(28, "days").valueOf(),
                              zIndex: 100,
                              label: {
                                  text: translate("keywordanalysis.organic2.clicks.over.time.graph.plotLine.last28"),
                                  align: "left",
                                  y: 20,
                                  x: -50,
                                  rotation: 0,
                                  style: { color: colorsPalettes.carbon[200] },
                              },
                          }
                        : {},
                ],
                minPadding: 0,
                maxPadding: 0,
                ...(timeGranularity !== GranularityNames.DAILY && {
                    tickPositioner: function () {
                        const min = this.min;
                        const max = this.max;
                        const range = max - min;
                        const idealTickCount = 6;
                        const tickInterval = range / idealTickCount;

                        const ticks = Array.from(
                            { length: idealTickCount + 1 },
                            (_, i) => min + i * tickInterval,
                        ).filter((tick) => tick <= max);

                        if (ticks.length > 8) {
                            return ticks.filter((_, index) => index % 3 === 0);
                        }
                        if (ticks.length > 6) {
                            return ticks.filter((_, index) => index % 2 === 0);
                        }

                        return ticks;
                    },
                }),
            },
        },
    ]);
};
